import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import {
  CircularProgress, Container, ThemeProvider, Typography,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';

import {
  ContentContainer,
  HeaderBox,
  Title,
  SubmitButtonBox,
} from './UserInfoConfirmation.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import {
  Product, getProductMinAge, getPdtUid,
} from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { EDIT_PATIENT_INFO, ACTIVATE_USER_BY_ACCESS_CODE } from '@/apollo/mutations';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { requestAuthCode } from '@/utils/sparkActivationApi';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import {
  EMPLOYER_ORG_SLUG_KEY, LANDING_PAGE_ROUTE, MINIMUM_BIRTH_YEAR, REDIRECT_TO_SESSION_KEY,
} from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import {
  getAge, validateZipCode, validateEmployeeId, EmployeeIdValidationRules,
} from '@/utils/stringValidators';
import { parseDate, formatDateString } from '@/utils/stringUtils';
import { formatDateInput } from '@/utils/inputHelpers';
import { logError } from '@/utils/logError';

const EDIT_PATIENT_CHANGE_REASON = 'updating patient info in cortex from spark in app activation flow';

/** Show first 3 chars, mask the rest (e.g., '837129' → '837***'). */
const maskAccessCode = (code?: string): string => {
  if (!code) { return '[none]'; }
  return code.length > 3 ? `${code.slice(0, 3)}${'*'.repeat(code.length - 3)}` : code;
};

type ActivationFlow = 'sms' | 'email';

interface UserInfoLocationState {
  accessCode?: string;
  phoneNumber?: string;
  patientUid?: string;
  flow?: ActivationFlow;
}

type SchemaErrorMessages = {
  firstName: string;
  lastName: string;
  dob: string;
  dobMinAge: string;
  phone: string;
  zipCode: string;
  employeeId: string;
};

const createSchema = (
  errorMessages: SchemaErrorMessages,
  minAge: number,
  showPhoneField: boolean,
  showZipCode: boolean,
  showEmployeeId: boolean,
  employeeIdRules?: EmployeeIdValidationRules,
) => {
  const base = z.object({
    firstName: z.string({ required_error: errorMessages.firstName })
      .min(1, { message: errorMessages.firstName }),
    lastName: z.string({ required_error: errorMessages.lastName })
      .min(1, { message: errorMessages.lastName }),
    dob: z.string({ required_error: errorMessages.dob })
      .length(14, { message: errorMessages.dob })
      .refine(
        (dob) => {
          const parsedDate = parseDate(dob);
          const date = new Date(parsedDate);
          const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= MINIMUM_BIRTH_YEAR;
          return isValidDate && getAge(dob) >= minAge;
        },
        (dob) => {
          const parsedDate = parseDate(dob);
          const date = new Date(parsedDate);
          const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= MINIMUM_BIRTH_YEAR;
          return { message: isValidDate ? errorMessages.dobMinAge : errorMessages.dob };
        },
      ),
    phoneNumber: showPhoneField
      ? z.string().optional().refine(
        (v) => !v || isValidPhoneNumber(v),
        { message: errorMessages.phone },
      )
      : z.string().optional(),
    zipCode: showZipCode
      ? z.string()
        .min(1, { message: errorMessages.zipCode })
        .refine((v) => validateZipCode(v), { message: errorMessages.zipCode })
      : z.string().optional(),
    employeeId: showEmployeeId
      ? z.string()
        .min(1, { message: errorMessages.employeeId })
        .refine(
          (id) => {
            if (!employeeIdRules?.validationEnabled) { return id.trim().length > 0; }
            return validateEmployeeId(id, employeeIdRules).isValid;
          },
          (id) => ({
            message: validateEmployeeId(id, employeeIdRules).errorMessage ?? errorMessages.employeeId,
          }),
        )
      : z.string().optional(),
  });

  return base;
};

type UserInfoForm = z.infer<ReturnType<typeof createSchema>>;

const UserInfoConfirmationInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const posthog = usePostHog();
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { state: profileData, search } = useLocation<UserInfoLocationState>();
  const [submitError, setSubmitError] = useState('');
  const [isHandingOff, setIsHandingOff] = useState(false);

  const [editPatientInfo, { loading: isSubmitting }] = useMutation(EDIT_PATIENT_INFO);
  const [activateUserByAccessCode] = useMutation(ACTIVATE_USER_BY_ACCESS_CODE);

  const program = getProgram(product);
  const minAge = getProductMinAge(product);
  const showPhoneField = profileData?.flow === 'email';

  const orgSlug = new URLSearchParams(search).get('org_slug')
    || sessionStorage.getItem(EMPLOYER_ORG_SLUG_KEY)
    || '';
  const isEmployerFlow = !!orgSlug;

  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(
    ELIGIBILITY_CONFIGURATION_QUERY,
    {
      variables: { organizationSlug: orgSlug, pdtUid: getPdtUid(product) },
      skip: !isEmployerFlow,
    },
  );

  const config = eligibilityData?.eligibilityConfiguration;

  const showZipCode = isEmployerFlow ? (config?.collectZipcodeForEligibility ?? false) : false;
  const showEmployeeId = isEmployerFlow ? (config?.hasEmployeeId ?? false) : false;

  const employeeIdRules: EmployeeIdValidationRules = {
    validationEnabled: config?.employeeIdValidationEnabled,
    allowedChars: config?.employeeIdAllowedChars,
    minLength: config?.employeeIdMinLength,
    maxLength: config?.employeeIdMaxLength,
    errorMessage: config?.employeeIdValidationErrorMessage,
  };

  const employeeIdLabel = config?.employeeIdFieldRename
    || t('coverage.pre_onboarding.user_info_confirmation.employee_id_label');

  const schema = useMemo(() => createSchema({
    firstName: t('coverage.errors.first_name_error'),
    lastName: t('coverage.errors.last_name_error'),
    dob: t('coverage.errors.dob_error'),
    dobMinAge: t('coverage.pre_onboarding.user_info_confirmation.dob_error_min_age', { minAge }),
    phone: t('coverage.errors.phone_error'),
    zipCode: t('coverage.pre_onboarding.user_info_confirmation.zip_code_error'),
    employeeId: t('coverage.pre_onboarding.user_info_confirmation.employee_id_error'),
  }, minAge, showPhoneField, showZipCode, showEmployeeId, employeeIdRules),
  [t, minAge, showPhoneField, showZipCode, showEmployeeId, eligibilityData]);

  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<UserInfoForm>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
      phoneNumber: '',
      zipCode: '',
      employeeId: '',
    },
  });

  useEffect(() => {
    posthog?.capture('SparkUserInfoConfirmationScreenViewed', { program });
  }, [posthog, program]);

  const initiateSessionHandoff = async () => {
    const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

    // Browser context: no in-app deep link available — route to download page.
    if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
      history.replace(`/appstore/${product}`);
      return;
    }

    const {
      data: { session: refreshedSession },
      error: refreshError,
    } = await getSupabaseClient().auth.refreshSession();
    if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
      throw new Error(`refreshSession failed: ${refreshError?.message ?? 'no session'}`);
    }
    const { exchangeToken } = await requestAuthCode(
      refreshedSession.access_token,
      refreshedSession.refresh_token,
    );
    const deepLinkUrl = new URL(redirectTo);
    deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
    if (profileData?.patientUid) {
      deepLinkUrl.searchParams.set('limbix_user_uid', profileData.patientUid);
    }
    const deepLink = deepLinkUrl.toString();

    window.location.href = deepLink;
  };

  const onSubmit = async (data: UserInfoForm) => {
    setSubmitError('');

    try {
      const { data: { session } } = await getSupabaseClient().auth.getSession();

      if (!session?.access_token) {
        logError('[UserInfoConfirmation]', 'no active Supabase session found at submission time');
        setSubmitError(t('coverage.errors.generic_error'));
        return;
      }

      const variables = {
        changeReason: EDIT_PATIENT_CHANGE_REASON,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: formatDateString(data.dob),
        phone: showPhoneField ? (data.phoneNumber || null) : (profileData?.phoneNumber ?? null),

      };

      const response = await editPatientInfo({
        variables,
        context: {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      });
      const result = response.data?.editPatientInfo;

      if (result?.success) {
        posthog?.capture('SparkUserInfoConfirmationSubmitted', { program });

        // Activate user by access code in Cortex if access code is present.
        // This runs after editPatientInfo so the Cortex patient is fully linked.
        if (profileData?.accessCode) {
          try {
            const activateResponse = await activateUserByAccessCode({
              variables: { accessCode: profileData.accessCode },
              context: { headers: { Authorization: `Bearer ${session.access_token}` } },
            });
            if (!activateResponse.data?.activateUserByAccessCode?.success) {
              logError(
                '[UserInfoConfirmation]',
                'activateUserByAccessCode returned success: false',
                undefined,
                { accessCode: maskAccessCode(profileData.accessCode) },
              );
              setSubmitError(t('coverage.errors.generic_error'));
              return;
            }
          } catch (err) {
            logError(
              '[UserInfoConfirmation]',
              'activateUserByAccessCode failed',
              err,
              { accessCode: maskAccessCode(profileData.accessCode) },
            );
            setSubmitError(t('coverage.errors.generic_error'));
            return;
          }
        }

        try {
          setIsHandingOff(true);
          await initiateSessionHandoff();
        } catch (handoffError) {
          logError('[UserInfoConfirmation]', 'Session handoff failed', handoffError);
          posthog?.capture('SparkSessionHandoffFailed', { program });
          setSubmitError(t('coverage.errors.generic_error'));
          history.push(LANDING_PAGE_ROUTE);
        } finally {
          setIsHandingOff(false);
        }
        return;
      }

      posthog?.capture('SparkUserInfoConfirmationErrored', { program });
      setSubmitError(t('coverage.errors.generic_error'));
    } catch (error) {
      logError('[UserInfoConfirmation]', 'submit failed', error);
      posthog?.capture('SparkUserInfoConfirmationErrored', { program });
      setSubmitError(t('coverage.errors.generic_error'));
    }
  };

  const isSubmitEnabled = isValid && !isSubmitting && !isHandingOff;

  if (isEmployerFlow && eligibilityLoading) {
    return (
      <ContentContainer>
        <Header />
        <Container
          maxWidth="xs"
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CircularProgress data-testid="eligibility-loading" />
        </Container>
        <Footer />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.user_info_confirmation.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.user_info_confirmation.sub_title')}
          </Typography>
        </HeaderBox>
        {/* First Name */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.f_name_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.first_name_placeholder')}
                helperText={errors.firstName?.message}
                error={!!errors.firstName}
                onFocus={() => clearErrors('firstName')}
                inputProps={{ 'data-testid': 'first-name-input' }}
              />
            )}
          />

          {/* Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.l_name_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.last_name_placeholder')}
                helperText={errors.lastName?.message}
                error={!!errors.lastName}
                onFocus={() => clearErrors('lastName')}
                inputProps={{ 'data-testid': 'last-name-input' }}
              />
            )}
          />

          {/* Date of Birth */}
          <Controller
            name="dob"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.dob_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.dob_placeholder')}
                helperText={errors.dob?.message}
                error={!!errors.dob}
                onFocus={() => clearErrors('dob')}
                inputProps={{
                  'data-testid': 'dob-input',
                  maxLength: 14,
                  inputMode: 'numeric',
                }}
                FormHelperTextProps={
                  { 'data-testid': 'dob-error' } as React.HTMLAttributes<HTMLParagraphElement>
                }
                onChange={(e) => {
                  const value = formatDateInput(e.target.value);
                  field.onChange(value);
                }}
              />
            )}
          />

          {showZipCode && (
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('coverage.pre_onboarding.user_info_confirmation.zip_code_label')}
                  placeholder={t('coverage.pre_onboarding.user_info_confirmation.zip_code_placeholder')}
                  helperText={errors.zipCode?.message}
                  error={!!errors.zipCode}
                  onFocus={() => clearErrors('zipCode')}
                  inputProps={{
                    'data-testid': 'zip-code-input',
                    inputMode: 'numeric',
                    maxLength: 5,
                  }}
                />
              )}
            />
          )}

          {showEmployeeId && (
            <Controller
              name="employeeId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={employeeIdLabel}
                  placeholder={t('coverage.pre_onboarding.user_info_confirmation.employee_id_placeholder')}
                  helperText={errors.employeeId?.message}
                  error={!!errors.employeeId}
                  onFocus={() => clearErrors('employeeId')}
                  inputProps={{ 'data-testid': 'employee-id-input' }}
                />
              )}
            />
          )}

          {showPhoneField && (
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneNumberInput
                  id="phoneNumber"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('coverage.sleepio.patient.phone_label')}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required={false}
                  data-testid="phone-input"
                />
              )}
            />
          )}

          <SubmitButtonBox>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              rounded
              type="submit"
              data-testid="submit-button"
              disabled={!isSubmitEnabled}
            >
              {(isSubmitting || isHandingOff)
                ? t('coverage.pre_onboarding.user_info_confirmation.submitting')
                : t('coverage.submit')}
            </Button>
          </SubmitButtonBox>

          {submitError && (
            <Typography color="error" data-testid="submit-error" style={{ marginTop: 8 }}>
              {submitError}
            </Typography>
          )}
        </form>

        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const UserInfoConfirmation: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <UserInfoConfirmationInner />
  </ThemeProvider>
);

export default UserInfoConfirmation;
